import type { BlogPost } from './types';

export const post: BlogPost = {
  slug: 'moneyline-explained-like-youre-hungover',
  title: 'Moneyline, explained like you\'re hungover',
  excerpt:
    'The simplest bet in the building, walked through slowly and with a glass of water. No spread, no math gymnastics, no judgment.',
  author: 'Sloane',
  date: '2026-06-23',
  category: 'Betting Basics',
  evergreen: true,
  tags: ['basics', 'moneyline', 'odds', 'underdogs'],
  body: `# Moneyline, explained like you're hungover

Okay. Sit down. Drink the water first. We are going to do this gently because the moneyline is the one bet that does not require your brain to be at full power, which makes it the perfect place to start.

A moneyline bet is just: **who wins.** That is the whole thing. No spread, no "by how much," no half-points trying to ruin your night. You pick the team. They win, you win. They lose, you lose. It is the bet your most sports-illiterate friend can place correctly, which is the nicest thing we will say about it.

The only part that looks scary is the number next to the team, and it is not scary, it is just two different outfits on the same idea.

## Plus and minus

A **minus** number is the favorite. It tells you how much you have to risk to win $100. If a team is -150, you bet $150 to win $100. The team is expected to win, so the book makes you pay up for the privilege of betting on the obvious.

A **plus** number is the underdog. It tells you how much you win on a $100 bet. If a team is +150, you bet $100 to win $150. The team is expected to lose, so the payout is bigger to make it interesting. The plus sign is the universe saying "are you sure," and sometimes you are.

You do not need to bet $100, by the way. That is just the unit the math is written in. The ratio holds at any size. A $10 bet at +150 wins you $15. A $10 bet at -150 wins you about $6.67. The numbers scale; your dignity does not have to.

## The percentage underneath

Here is the part that takes betting from "guessing" to "kind of knowing what you're doing." That number is also a probability in disguise. -150 is the book quietly saying this team wins about 60% of the time. +150 is about 40%. Once you can read the price as a percentage, you stop asking "do I think they win" and start asking "do they win *more often than the number says.*" That second question is the entire game. The first one is just having a favorite team.

## When the moneyline earns its price

When to actually use the moneyline: when you like an underdog to win outright and the spread feels like a tax, or when you like a favorite but do not trust them to win by a specific margin. A team can win the game and lose you the spread bet by a backdoor field goal. The moneyline does not care about style points. It cares about the final, and so, on certain nights, do you.

When to skip it: when the favorite is so expensive that you are risking $400 to win $100. At that point you are not betting, you are loaning a sportsbook money and hoping nothing weird happens. Something weird is always one missed extra point away.

That is moneyline. Who wins, plus or minus, percentage in disguise. You can lie back down now. You did great.`,
};
